import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  portageService,
  type CreatePortageCompanyData,
  type SuggestPortageContactData,
} from '@/services/portageService'
import { useApiReady } from '@/hooks/useApiReady'
import { useAuthStore } from '@/store/useAuthStore'
import { profileKeys } from '@/hooks/queries/useProfile'
import { freelancerDetailKeys } from '@/hooks/queries/useFreelancerDetail'

export const portageCompanyKeys = {
  all: ['portageCompanies'] as const,
  options: (userId?: string | null, search?: string) =>
    [...portageCompanyKeys.all, 'options', userId, search] as const,
  contacts: (companyId: string | null | undefined, search?: string) =>
    [...portageCompanyKeys.all, 'contacts', companyId, search] as const,
}

export function usePortageCompanyOptions(search?: string) {
  const apiReady = useApiReady()
  const userId = useAuthStore((state) => state.user?.id)

  return useQuery({
    queryKey: portageCompanyKeys.options(userId, search),
    queryFn: () => portageService.getPortageCompanyOptions(search),
    enabled: apiReady && !!userId,
  })
}

export function usePortageContactOptions(
  portageCompanyId: string | null | undefined,
  search?: string
) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: portageCompanyKeys.contacts(portageCompanyId, search),
    queryFn: () => portageService.getPortageContactOptions(portageCompanyId!, search),
    enabled: apiReady && !!portageCompanyId,
  })
}

export function useCreatePortageCompany() {
  const queryClient = useQueryClient()

  return useMutation<
    Awaited<ReturnType<typeof portageService.createPortageCompany>>,
    Error,
    CreatePortageCompanyData
  >({
    mutationFn: (data: CreatePortageCompanyData) => portageService.createPortageCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portageCompanyKeys.all })
    },
  })
}

export function useSuggestPortageContact(portageCompanyId: string | null | undefined) {
  const queryClient = useQueryClient()

  return useMutation<
    Awaited<ReturnType<typeof portageService.suggestPortageContact>>,
    Error,
    SuggestPortageContactData
  >({
    mutationFn: (data: SuggestPortageContactData) =>
      portageService.suggestPortageContact(portageCompanyId!, data),
    onSuccess: (contact) => {
      // Options = verified uniquement : upsert cache puis invalidate pour sync.
      if (portageCompanyId && contact?.id && contact.status === 'verified') {
        queryClient.setQueriesData(
          { queryKey: [...portageCompanyKeys.all, 'contacts', portageCompanyId] },
          (prev: Awaited<ReturnType<typeof portageService.getPortageContactOptions>> | undefined) => {
            const list = Array.isArray(prev) ? prev : []
            const nextItem = {
              id: contact.id,
              first_name: contact.first_name,
              last_name: contact.last_name,
              email: contact.email,
              status: contact.status,
            }
            const idx = list.findIndex((c) => c.id === contact.id)
            if (idx >= 0) {
              const copy = [...list]
              copy[idx] = nextItem
              return copy
            }
            return [nextItem, ...list]
          }
        )
      }
      queryClient.invalidateQueries({
        queryKey: [...portageCompanyKeys.all, 'contacts', portageCompanyId],
      })
    },
  })
}

export function useResendPortageInvitation() {
  const queryClient = useQueryClient()

  return useMutation<
    { skipped: boolean; invitation_sent_at?: string },
    Error,
    { companyId: string; freelancerId?: string }
  >({
    mutationFn: ({ companyId, freelancerId }) =>
      portageService.resendPortageInvitation(companyId, { freelancerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.freelancer() })
      queryClient.invalidateQueries({ queryKey: freelancerDetailKeys.all })
    },
  })
}

export function useUpdatePendingPortageAffiliationContact() {
  const queryClient = useQueryClient()

  return useMutation<
    Awaited<ReturnType<typeof portageService.updatePendingAffiliationContact>>,
    Error,
    { portageCompanyId: string; portageContactId: string; freelancerId: string }
  >({
    mutationFn: ({ portageCompanyId, portageContactId, freelancerId }) =>
      portageService.updatePendingAffiliationContact(portageCompanyId, {
        portage_contact_id: portageContactId,
        freelancer_id: freelancerId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.freelancer() })
      queryClient.invalidateQueries({ queryKey: freelancerDetailKeys.all })
      queryClient.invalidateQueries({ queryKey: portageCompanyKeys.all })
    },
  })
}

export function useResendPortageMissionSignature() {
  return useMutation<
    { skipped: boolean },
    Error,
    { missionId: string; addendumId?: string }
  >({
    mutationFn: ({ missionId, addendumId }) =>
      portageService.resendPortageMissionSignature(missionId, { addendumId }),
  })
}
